import React, { useState, useEffect, useCallback } from 'react';
import { FolderKanban, RefreshCw, Copy, Check } from 'lucide-react';
import { getMyProjects } from '../api';

const RL_FIELDS = [
  { key: 'per_minute', label: 'min' },
  { key: 'per_hour', label: 'hour' },
  { key: 'per_6h', label: '6h' },
  { key: 'per_day', label: 'day' },
];

export default function MyProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setProjects((await getMyProjects()).data.projects || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (id) => {
    navigator.clipboard?.writeText(id);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-fg">My Teams</h1>
          <p className="text-fg-muted text-sm mt-1">The teams you belong to. Select a team in your client (LatentCode / LatentWorks) to use its models and limits.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg text-sm"><RefreshCw className="w-4 h-4" />Refresh</button>
      </div>

      {loading ? (
        <div className="text-center text-fg-muted py-16">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="text-center text-fg-muted py-16 bg-panel border border-panel-border rounded-xl">You're not a member of any team yet — ask your admin to add you.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projects.map((p) => (
            <div key={p.project_id} className="bg-panel border border-panel-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <FolderKanban className="w-4 h-4 text-orange" />
                <span className="text-fg font-semibold truncate">{p.name}</span>
                {p.tier && <span className="px-2 py-0.5 bg-orange/10 text-orange text-xs rounded">{p.tier}</span>}
              </div>

              {/* project_id to paste into the client config (X-Project-Id) */}
              <button onClick={() => copy(p.project_id)}
                className="flex items-center gap-2 text-xs text-fg-muted hover:text-fg font-mono mt-2"
                title="Copy team id">
                {p.project_id}
                {copied === p.project_id ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
              </button>

              <div className="mt-3">
                <div className="text-xs text-fg-muted mb-1">Rate limits</div>
                <div className="flex flex-wrap gap-2">
                  {RL_FIELDS.map((r) => (
                    <span key={r.key} className="px-2 py-0.5 bg-field border border-field-border rounded text-xs text-fg">
                      {r.label}: {p.rate_limits?.[r.key] ? p.rate_limits[r.key] : '∞'}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-3">
                <div className="text-xs text-fg-muted mb-1">Models ({p.models?.length || 0})</div>
                <div className="flex flex-wrap gap-1">
                  {(p.models || []).length === 0
                    ? <span className="text-fg-muted text-xs">no models in this team's tier</span>
                    : p.models.map((m) => <span key={m} className="px-2 py-0.5 bg-orange/10 text-orange text-xs rounded font-mono">{m}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
